import { makeStyles } from '@material-ui/core'
import CopilotReportIcon from 'components/CopilotControl/icons/CopilotReportIcon'
import OralHygieneReportIcon from 'components/CopilotControl/icons/OralHygieneReportIcon'
import TxTrackingReportIcon from 'components/CopilotControl/icons/TxTrackingReportIcon'
import useQueryParams from 'hooks/useQueryParams'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import useCopilotReports, { CopilotReports } from '../hooks/useCopilotReports'

const ACTIVE_COLOR = '#3C52EF'
const INACTIVE_COLOR = '#9CA3B2'

const useStyles = makeStyles(() => ({
  // Figma frame 217: 72px rail, 56px between entries, Dazzed SemiBold 8px labels.
  root: {
    flexShrink: 0,
    width: 72,
    height: '100%',
    paddingTop: 18,
    background: '#FFFFFF',
    boxSizing: 'border-box',
    boxShadow: 'inset -1px 0 #E6E6E6',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 22
  },
  reportItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit'
  },
  icon: {
    display: 'flex',
    height: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  label: {
    fontFamily: 'Dazzed',
    fontSize: 8,
    fontWeight: 600,
    lineHeight: '8px',
    textAlign: 'center'
  }
}))

const REPORT_ITEMS = {
  [CopilotReports.Copilot]: {
    labelKey: 'pages.oralHygieneReport.reportsNav.copilot',
    Icon: CopilotReportIcon
  },
  [CopilotReports.OralHygiene]: {
    labelKey: 'pages.oralHygieneReport.reportsNav.oralHygiene',
    Icon: OralHygieneReportIcon
  },
  // Figma frame 220 sets Tx Tracking 60px below Oral Hygiene, 4px more than the 56px step above it.
  [CopilotReports.TxTracking]: {
    labelKey: 'pages.oralHygieneReport.reportsNav.txTracking',
    Icon: TxTrackingReportIcon,
    marginTop: 4
  }
}

/**
 * Inner navigation between the Copilot reports. Rendered only when the selected
 * account owner has more than one report enabled.
 */
const CopilotReportsNav = ({ activeReport }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const enabledReports = useCopilotReports()
  const { queryParams } = useQueryParams()

  if (enabledReports.length < 2) {
    return null
  }

  // Keep an internal user's doctor override when switching reports. Without it the target report
  // falls back to the signed-in account, and its eligibility guard redirects to Patients.
  const search = queryParams.copilotDoctorId
    ? queryString.stringify({ copilotDoctorId: queryParams.copilotDoctorId })
    : ''

  return (
    <nav className={classes.root} aria-label={t('pages.oralHygieneReport.reportsNav.ariaLabel')}>
      {enabledReports.map(report => {
        const { labelKey, Icon, marginTop } = REPORT_ITEMS[report.key]
        const isActive = report.key === activeReport
        const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR
        return (
          <button
            type="button"
            key={report.key}
            className={classes.reportItem}
            style={{ marginTop }}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => !isActive && history.push({ pathname: report.route, search })}
          >
            <span className={classes.icon}>
              <Icon color={color} />
            </span>
            <span className={classes.label} style={{ color }}>
              {t(labelKey)}
            </span>
          </button>
        )
      })}
    </nav>
  )
}

export default CopilotReportsNav
